import { type ClientAndDataT } from '@ruminati/types_v2/client'
import { type OrganisationCreateClientT } from '@ruminati/types_v2/organisation'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import { CommonApiV2Instance } from '@/services/common'
import { useOrganisationStore } from '@/stores/organisations'
import { usePartnerOrgStore } from '@/stores/partnerOrg'

import { defaultQueryOptions } from './queryDefaults'

export const queryKeysClients = {
  all: (organisationId: string | undefined) => ['clients', organisationId] as const,
}

export function useClientsQuery() {
  const clientsStore = usePartnerOrgStore()
  const organisationStore = useOrganisationStore()
  const queryEnabled = computed(
    () => !!organisationStore.currentOrgId && organisationStore.currentOrg?.type === 'records',
  )

  return useQuery({
    queryKey: computed(() => queryKeysClients.all(organisationStore.currentOrgId)),
    queryFn: async ({ signal }) => {
      const clients = await CommonApiV2Instance.get(
        `organisations/${organisationStore.currentOrgId}/clients`,
        { signal },
      ).json<Array<ClientAndDataT>>()
      clientsStore.setClients(clients)
      return clients
    },
    enabled: queryEnabled,
    ...defaultQueryOptions,
  })
}

export function useCreateClient() {
  const clientsStore = usePartnerOrgStore()
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (details: OrganisationCreateClientT) => {
      if (!organisationStore.currentOrg) throw Error('No current org ID')
      return CommonApiV2Instance.post(`organisations/${organisationStore.currentOrgId}/clients`, {
        json: details,
      }).json<ClientAndDataT>()
    },
    onSuccess: (newClient) => {
      clientsStore.addClient(newClient)
    },
  })
}
